import jsPDF from "jspdf";
import UIConfig from "../../constants/UIConfig";
import magicHealthLogo from "../../../assets/images/magicHealthLogo.png";

const MARGIN = 14;
const BRAND = [27, 69, 107]; // #1b456b
const BRAND_LIGHT = [237, 244, 250]; // #edf4fa
const TEXT_DARK = [40, 46, 54];
const TEXT_MUTED = [120, 130, 140];
const LINK_BLUE = [0, 102, 204]; // #0066cc
const BORDER = [222, 232, 241];

const NA = (v) =>
  v === undefined || v === null || v === "" ? "N/A" : String(v);

const loadImageSafe = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    // Never let a broken/slow logo hang PDF generation — just skip it.
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Builds the Call Review feedback summary PDF using jsPDF — the same PDF
 * package already used across the app (see DownloadPDF.js / InsightPDFDownload.js).
 * Hand-drawn layout (cards, tables, badges) since no table/UI plugin is a
 * project dependency. Returns the jsPDF instance so callers can `.save()` it
 * or pull a blob/dataurl for the future "Email to Agent" integration.
 * Async because the branding logos are loaded before drawing the header.
 */
export const buildCallReviewPDF = async ({
  vonageCallId,
  magicHealthId,
  agentName,
  memberName,
  reviewDate,
  reviewerName,
  overallScore,
  maxScore,
  overallAverage,
  sections = [],
  whatWentWell,
  whatCanImprove,
  generalComments,
  // When true the Scores section is replaced by an Executive Summary — used
  // for call categories that have no CRS scoring.
  feedbackOnly = false,
  executiveSummary = "",
}) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const footerReserve = 14;
  let y = 0;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - footerReserve) {
      doc.addPage();
      y = MARGIN;
    }
  };

  /* ── Header band ── */
  const drawHeader = (clientLogo, mHealthLogo) => {
    const bandTop = 16;

    if (clientLogo) {
      const cLogoH = 7;
      const cLogoW = (clientLogo.width / clientLogo.height) * cLogoH;
      doc.addImage(clientLogo, "PNG", MARGIN, 6, cLogoW, cLogoH);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(150);
    doc.text("Powered by", pageWidth - 50, 10.5, { align: "right" });
    if (mHealthLogo) {
      const mLogoH = 5;
      const mLogoW = (mHealthLogo.width / mHealthLogo.height) * mLogoH;
      doc.addImage(
        mHealthLogo,
        "PNG",
        pageWidth - MARGIN - mLogoW,
        6.5,
        mLogoW,
        mLogoH,
      );
    }

    const bandH = 10;
    doc.setFillColor(...BRAND);
    doc.rect(0, bandTop, pageWidth, bandH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(255, 255, 255);
    doc.text("Call Review Feedback Summary", MARGIN, bandTop + 6.8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(210, 224, 236);
    doc.text(
      `Generated ${new Date().toLocaleDateString("en-US")}`,
      pageWidth - MARGIN,
      bandTop + 6.8,
      { align: "right" },
    );

    y = bandTop + bandH + 6;
  };

  const addSectionHeading = (text) => {
    ensureSpace(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...BRAND);
    doc.text(text, MARGIN, y);
    y += 2;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);
    y += 5;
  };

  const addDetailsCard = (rows) => {
    const rowH = 11;
    const cardH = Math.ceil(rows.length / 2) * rowH + 5;
    ensureSpace(cardH);

    doc.setFillColor(...BRAND_LIGHT);
    doc.roundedRect(MARGIN, y, contentWidth, cardH, 2, 2, "F");

    const colW = contentWidth / 2;
    rows.forEach(([label, value], idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const cellX = MARGIN + col * colW + 6;
      const cellY = y + 6.5 + row * rowH;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...TEXT_MUTED);
      doc.text(label.toUpperCase(), cellX, cellY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...TEXT_DARK);
      doc.text(NA(value), cellX, cellY + 4.5);
    });

    y += cardH + 6;
  };

  const SALESFORCE_VONAGE_HINT =
    "(Copy/paste this ID in the top search bar in Salesforce to find this call's recording/transcript)";
  const addVonageIdRow = (value) => {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    const hintLines = doc.splitTextToSize(
      SALESFORCE_VONAGE_HINT,
      contentWidth - 12,
    );
    const cardH = 15 + hintLines.length * 3.8;
    ensureSpace(cardH);

    doc.setFillColor(...BRAND_LIGHT);
    doc.roundedRect(MARGIN, y, contentWidth, cardH, 2, 2, "F");

    const cellX = MARGIN + 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("VONAGE CALL ID", cellX, y + 6.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_DARK);
    doc.text(NA(value), cellX, y + 11);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...LINK_BLUE);
    hintLines.forEach((line, i) => {
      doc.text(line, cellX, y + 15.5 + i * 3.8);
    });

    y += cardH + 6;
  };

  /* ── Overall score banner ── */
  const addOverallScoreBanner = () => {
    const hasAverage =
      overallAverage?.outOf5 !== undefined ||
      overallAverage?.percentage !== undefined;
    const bannerH = hasAverage ? 16 : 12;
    ensureSpace(bannerH + 5);

    doc.setFillColor(...BRAND);
    doc.roundedRect(MARGIN, y, contentWidth, bannerH, 2, 2, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("OVERALL SCORE", MARGIN + 6, y + 7.5);

    doc.setFontSize(13);
    const overallText = maxScore
      ? `${overallScore ?? 0} / ${maxScore} points`
      : "N/A";
    doc.text(overallText, pageWidth - MARGIN - 6, y + 8, {
      align: "right",
    });

    // Grey "Average: X / 5 • Y%" line — commented out. It's just the same
    // achieved/max ratio the backend rescales to /5 and %, redundant with
    // the points already shown above.
    // if (hasAverage) {
    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(8);
    //   doc.setTextColor(210, 224, 236);
    //   const avgParts = [];
    //   if (overallAverage?.outOf5 !== undefined) avgParts.push(`Average: ${overallAverage.outOf5} / 5`);
    //   if (overallAverage?.percentage !== undefined) avgParts.push(`${overallAverage.percentage}%`);
    //   doc.text(avgParts.join("  •  "), pageWidth - MARGIN - 6, y + 13, { align: "right" });
    // }

    y += bannerH + 5;
  };

  /* ── Per-section criteria table ── */
  const addSectionTable = (section) => {
    const earned = (section.values || []).reduce(
      (s, v) => s + (v === "NA" ? 0 : Number(v) || 0),
      0,
    );
    const fields = section.fields || [];
    // Whole section marked N/A → show "N/A" instead of "0 / X pts".
    const allNA =
      (section.values || []).length > 0 &&
      (section.values || []).every((v) => v === "NA");

    const hasSummary =
      section.summary?.outOf5 !== undefined ||
      section.summary?.percentage !== undefined;
    const headerH = hasSummary ? 9.5 : 7.5;

    ensureSpace(headerH + 4);
    doc.setFillColor(...BRAND_LIGHT);
    doc.rect(MARGIN, y, contentWidth, headerH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND);
    doc.text(section.title, MARGIN + 3, y + 5.2);
    doc.text(
      allNA ? "N/A" : `${earned} / ${section.sectionTotal} pts`,
      pageWidth - MARGIN - 3,
      y + 5.2,
      { align: "right" },
    );

    // Grey "X / 5 • Y%" summary line — commented out. It's the same
    // achieved/max ratio the backend rescales to /5 and %, redundant with
    // the "earned / sectionTotal pts" already shown above.
    // if (hasSummary) {
    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(7.5);
    //   doc.setTextColor(...TEXT_MUTED);
    //   const summaryParts = [];
    //   if (section.summary?.outOf5 !== undefined) summaryParts.push(`${section.summary.outOf5} / 5`);
    //   if (section.summary?.percentage !== undefined) summaryParts.push(`${section.summary.percentage}%`);
    //   doc.text(summaryParts.join("  •  "), pageWidth - MARGIN - 3, y + 8.7, { align: "right" });
    // }

    y += headerH;

    const labelColWidth = contentWidth - 18;

    fields.forEach((field, idx) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(field.label, labelColWidth - 6);
      const rowH = Math.max(7, lines.length * 4.3 + 2.5);

      ensureSpace(rowH);

      if (idx % 2 === 1) {
        doc.setFillColor(249, 251, 253);
        doc.rect(MARGIN, y, contentWidth, rowH, "F");
      }

      doc.setTextColor(...TEXT_DARK);
      lines.forEach((line, lineIdx) => {
        doc.text(line, MARGIN + 3, y + 4.6 + lineIdx * 4.3);
      });

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND);
      const cellVal = section.values?.[idx];
      doc.text(
        cellVal === "NA" ? "N/A" : String(cellVal ?? 0),
        pageWidth - MARGIN - 3,
        y + 4.6,
        { align: "right" },
      );

      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, y + rowH, pageWidth - MARGIN, y + rowH);

      y += rowH;
    });

    y += 5;
  };

  /* ── Feedback text card with colored accent bar ── */
  /**
   * Reviewer text, laid out with the line structure they typed preserved.
   *
   * Each line the reviewer entered is wrapped on its own rather than handing
   * the whole block to splitTextToSize, which reflows everything into one
   * paragraph and loses the bullets and numbering that carry the reviewer's
   * meaning. Blank lines are kept as spacing, and a wrapped bullet is indented
   * to sit under its own text rather than back at the margin, so a long point
   * still reads as one point.
   */
  /**
   * Turn reviewer HTML into drawable lines.
   *
   * Feedback is authored in a rich text field, so it arrives as markup with
   * lists, numbering and emphasis. Those carry the reviewer's meaning — which
   * point is which, and what is nested under what — and the PDF has to show
   * the same shape as the email or the two disagree.
   *
   * Walks the DOM rather than regexing the markup: nesting depth and list
   * numbering are structural, and a regex cannot see them. DOMParser is a
   * browser API and this only ever runs in the browser.
   *
   * Produces { text, indent, bold, italic } per drawn line, which the caller
   * renders — this function does no drawing, so wrapping and pagination stay
   * in one place.
   */
  const fontStyleFor = (bold, italic) =>
    bold && italic ? "bolditalic" : bold ? "bold" : italic ? "italic" : "normal";

  // Width of a fragment in ITS OWN font. Emphasis changes the metrics, so a
  // bold word measured in the regular face comes out short and the rest of the
  // line creeps left.
  const measureRun = (text, bold, italic) => {
    doc.setFont("helvetica", fontStyleFor(bold, italic));
    return doc.getTextWidth(text);
  };

  /**
   * Lay rich text out as lines of styled RUNS, not as one line per style.
   *
   * The previous version emitted an output line for every text node it met, so
   * "This is <b>bold</b> and <i>italic</i> in one sentence" arrived in the PDF
   * as four stacked lines. Emphasis inside a sentence is the normal case, so
   * that broke most formatted feedback.
   *
   * A line is now a list of runs drawn side by side on one baseline, each in
   * its own face. Wrapping measures every fragment in the face it will actually
   * be drawn in, so a line of mixed emphasis breaks where it really overflows.
   *
   * Whitespace is treated the way the reviewer meant it: newlines and tabs
   * between tags are formatting and collapse, but a run of real spaces is
   * content and is kept. The editor emits non-breaking spaces for spaces typed
   * deliberately, and those were being collapsed away along with the rest.
   */
  const layoutRichText = (html, maxWidth) => {
    const doc2 = new DOMParser().parseFromString(
      `<div>${html}</div>`,
      "text/html",
    );
    const root = doc2.body.firstChild;
    const INDENT_STEP = 5;

    const blocks = []; // { runs: [{text,bold,italic}], indent, hang }
    let current = null;

    const openLine = (indent, hang = 0) => {
      current = { runs: [], indent, hang };
      blocks.push(current);
      return current;
    };
    const closeLine = () => {
      current = null;
    };
    const blankLine = (indent) => {
      blocks.push({ runs: [], indent, hang: 0 });
      current = null;
    };
    const addText = (text, indent, style) => {
      if (!text) return;
      const line = current || openLine(indent);
      line.runs.push({
        text,
        bold: !!style.bold,
        italic: !!style.italic,
      });
    };

    const visit = (child, depth, style) => {
      const indent = depth * INDENT_STEP;

      if (child.nodeType === 3) {
        const t = child.textContent
          .replace(/ /g, " ") // a typed space the editor stored as nbsp
          .replace(/\t/g, " ")
          .replace(/ *[\r\n]+ */g, " "); // markup formatting, not content
        addText(t, indent, style);
        return;
      }
      if (child.nodeType !== 1) return;

      const tag = child.tagName.toLowerCase();
      const next = {
        bold: style.bold || tag === "b" || tag === "strong",
        italic: style.italic || tag === "i" || tag === "em",
      };

      if (tag === "br") {
        // Ends the current line; a second one in a row leaves a real gap.
        if (current) closeLine();
        else blankLine(indent);
        return;
      }
      if (tag === "ul" || tag === "ol") {
        closeLine();
        walk(child, depth + (depth > 0 ? 1 : 0), next);
        closeLine();
        return;
      }
      if (tag === "li") {
        closeLine();
        const parent = child.parentElement;
        const ordered = parent && parent.tagName.toLowerCase() === "ol";
        const index =
          Array.from(parent?.children || [])
            .filter((c) => c.tagName.toLowerCase() === "li")
            .indexOf(child) + 1;
        const marker = ordered ? `${index}. ` : "• ";

        // The marker is a run of its own, so emphasis inside the item styles
        // the words and not the bullet.
        const line = openLine(indent, measureRun(marker, false, false));
        line.runs.push({ text: marker, bold: false, italic: false });

        Array.from(child.childNodes).forEach((n) => {
          const isNested =
            n.nodeType === 1 &&
            ["ul", "ol"].includes(n.tagName.toLowerCase());
          if (!isNested) visit(n, depth, next);
        });
        closeLine();

        Array.from(child.children)
          .filter((n) => ["ul", "ol"].includes(n.tagName.toLowerCase()))
          .forEach((sub) => visit(sub, depth + 1, next));
        return;
      }
      if (tag === "p") {
        closeLine();
        walk(child, depth, next);
        closeLine();
        blankLine(indent); // paragraphs are separated; divs are not
        return;
      }
      if (tag === "div") {
        // The editor wraps each visual line in a div. Ending the line is right;
        // adding a gap as well double-spaced everything the reviewer typed.
        closeLine();
        walk(child, depth, next);
        closeLine();
        return;
      }
      walk(child, depth, next);
    };

    const walk = (node, depth, style) => {
      Array.from(node.childNodes).forEach((child) => visit(child, depth, style));
    };

    walk(root, 0, { bold: false, italic: false });

    // Wrap to the available width, measuring each fragment in its own face and
    // hanging continuations under a bullet's text rather than under the marker.
    const wrapped = [];
    blocks.forEach((block) => {
      if (!block.runs.length) {
        wrapped.push({ runs: [], indent: block.indent });
        return;
      }
      let cur = [];
      let curW = 0;
      let indent = block.indent;
      let avail = maxWidth - indent;

      const flush = () => {
        wrapped.push({ runs: cur, indent });
        cur = [];
        curW = 0;
        indent = block.indent + block.hang;
        avail = maxWidth - indent;
      };

      block.runs.forEach((run) => {
        // Keep the separators: the spaces between words are content here.
        run.text
          .split(/(\s+)/)
          .filter((p) => p !== "")
          .forEach((piece) => {
            const isSpace = !piece.trim();
            const w = measureRun(piece, run.bold, run.italic);
            if (!isSpace && cur.length && curW + w > avail) flush();
            // A wrapped line should not begin with the space that broke it.
            if (isSpace && !cur.length) return;
            cur.push({ text: piece, bold: run.bold, italic: run.italic, w });
            curW += w;
          });
      });
      if (cur.length) flush();
    });

    // Wrapping splits on whitespace, which leaves neighbouring fragments in the
    // same face. Rejoin them: one measurement of a whole string is truer than
    // the sum of its words, and it is one draw call instead of several.
    wrapped.forEach((line) => {
      const merged = [];
      line.runs.forEach((run) => {
        const prev = merged[merged.length - 1];
        if (prev && prev.bold === run.bold && prev.italic === run.italic) {
          prev.text += run.text;
        } else {
          merged.push({ ...run });
        }
      });
      merged.forEach((r) => {
        r.w = measureRun(r.text, r.bold, r.italic);
      });
      line.runs = merged;
    });

    // Collapse the trailing blank a block element leaves behind.
    while (wrapped.length && !wrapped[wrapped.length - 1].runs.length)
      wrapped.pop();
    return wrapped;
  };

  const looksLikeHtml = (v) =>
    /<\/?(b|strong|i|em|u|ul|ol|li|br|p|div)\b/i.test(String(v ?? ""));

  // Plain text saved before the rich editor shipped. Emits the same run-based
  // shape as layoutRichText so the card only has one thing to draw.
  const layoutReviewerText = (text, maxWidth) => {
    const out = [];
    doc.setFont("helvetica", "normal");
    text.split(/\r\n|\r|\n/).forEach((rawLine) => {
      if (!rawLine.trim()) {
        out.push({ runs: [], indent: 0 }); // blank line: keep the gap
        return;
      }
      // Leading spaces are indentation, not content — measure them and drop
      // them from the string, since PDF text drawing ignores them anyway.
      const leading = (rawLine.match(/^ */) || [""])[0].length;
      const indent = leading * 1.6;
      const body = rawLine.slice(leading);

      // Hanging indent for a bullet or number, so continuation lines align
      // with the text and not with the marker.
      const marker = body.match(/^([-*•]|\d+[.)])\s+/);
      const hang = marker ? doc.getTextWidth(marker[0]) : 0;

      const wrapped = doc.splitTextToSize(body, maxWidth - indent);
      wrapped.forEach((w, i) => {
        out.push({
          runs: [{ text: w, bold: false, italic: false, w: measureRun(w, false, false) }],
          indent: indent + (i > 0 ? hang : 0),
        });
      });
    });
    return out;
  };

  /* Feedback card geometry, named because the split logic below has to reason
     about it rather than just trust a single height number. */
  const CARD_LINE_H = 4.6; // baseline-to-baseline within the body
  const CARD_TOP = 13.5; // label sits at +7; first body line at +13.5
  const CARD_BOTTOM_PAD = 3.1; // breathing room under the last line
  // Below this, a fragment left at the foot of a page reads as a stray orphan,
  // so the break is taken early instead.
  const CARD_MIN_SPLIT_LINES = 3;

  // Height of a card holding `count` body lines. Deliberately identical to the
  // previous `count * 4.6 + 12`, so a card that already fits looks unchanged.
  const cardHeightFor = (count) =>
    Math.max(16, (count - 1) * CARD_LINE_H + CARD_TOP + CARD_BOTTOM_PAD);

  // How many body lines a card can show in `space` mm of remaining page.
  const cardLinesThatFit = (space) => {
    const usable = space - CARD_TOP - CARD_BOTTOM_PAD;
    return usable < 0 ? 0 : Math.floor(usable / CARD_LINE_H) + 1;
  };

  // Draw one card holding `slice`, at the current y, and advance past it.
  const drawFeedbackCard = (label, slice) => {
    const cardH = cardHeightFor(slice.length);

    doc.setFillColor(249, 251, 253);
    doc.roundedRect(MARGIN, y, contentWidth, cardH, 2, 2, "F");
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y, 1.6, cardH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND);
    doc.text(label, MARGIN + 7, y + 7);

    doc.setFontSize(9.5);
    doc.setTextColor(...TEXT_DARK);
    slice.forEach((line, idx) => {
      // A blank line still consumes its vertical space.
      if (!line.runs || !line.runs.length) return;
      const baseline = y + CARD_TOP + idx * CARD_LINE_H;
      let x = MARGIN + 7 + line.indent;
      // Runs sit side by side on one baseline, each in its own face, so a
      // sentence with emphasis inside it stays one sentence. Advancing by the
      // measured width is what keeps a run of spaces the reviewer typed — the
      // spaces draw nothing, but they still move the cursor.
      line.runs.forEach((run) => {
        doc.setFont("helvetica", fontStyleFor(run.bold, run.italic));
        doc.text(run.text, x, baseline);
        x += run.w != null ? run.w : doc.getTextWidth(run.text);
      });
    });
    doc.setFont("helvetica", "normal"); // leave the font as we found it

    y += cardH;
  };

  /**
   * Reviewer feedback, flowing across pages instead of jumping whole.
   *
   * Rich text made these cards long enough to outgrow the space left on a page.
   * Moving the card wholesale then stranded most of a page as blank — the taller
   * the feedback, the more was wasted. Now the card fills what is available and
   * carries the remainder onto the next page, which is what a reader expects of
   * a long passage anyway.
   *
   * Continuations repeat the label with "(continued)" so a card arriving at the
   * top of a page still says which question it answers.
   */
  const addFeedbackCard = (label, text) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    // Rich text from the editor is laid out from its structure; feedback saved
    // before the editor shipped is plain text and keeps the line-based path.
    // Both are needed — there is a lot of the latter.
    const bodyText =
      text && text.trim() ? text.replace(/^\s+|\s+$/g, "") : "No comments provided.";
    const lines = looksLikeHtml(bodyText)
      ? layoutRichText(bodyText, contentWidth - 14)
      : layoutReviewerText(bodyText, contentWidth - 14);

    let idx = 0;
    let isFirst = true;
    do {
      const left = lines.length - idx;
      let fits = cardLinesThatFit(pageHeight - footerReserve - y);

      // Take the break early when what is left of the page would only hold a
      // sliver — but never when the whole remainder already fits.
      if (fits < Math.min(left, CARD_MIN_SPLIT_LINES)) {
        doc.addPage();
        y = MARGIN;
        fits = cardLinesThatFit(pageHeight - footerReserve - y);
      }

      // `max(1, …)` guarantees forward progress even in the pathological case
      // where a fresh page somehow reports no room, so this cannot spin.
      const take = Math.max(1, Math.min(left, fits));
      drawFeedbackCard(isFirst ? label : `${label} (continued)`, lines.slice(idx, idx + take));
      idx += take;
      isFirst = false;
    } while (idx < lines.length);

    y += 6; // gap after the card, once, no matter how many pages it spanned
  };

  /* ── Build the document ── */
  // Logos are optional — failures resolve to null and the header just skips them.
  const [clientLogo, mHealthLogo] = await Promise.all([
    loadImageSafe(UIConfig?.branding?.logo),
    loadImageSafe(magicHealthLogo),
  ]);
  drawHeader(clientLogo, mHealthLogo);

  addSectionHeading("Call Details");
  addVonageIdRow(vonageCallId);
  // Review Date and Reviewer Name are dropped when empty rather than printed
  // with a blank value. They are only populated once someone has actually
  // reviewed the call; exporting or emailing it does not count, so a call
  // nobody has reviewed shows neither row instead of implying a review by
  // whoever produced the PDF.
  addDetailsCard(
    [
      ["Magic Health ID", magicHealthId],
      ["Agent Name", agentName],
      ["Member Name", memberName],
      ["Review Date", reviewDate],
      ["Reviewer Name", reviewerName],
    ].filter(([, value]) => (value ?? "").toString().trim() !== ""),
  );

  if (feedbackOnly) {
    // No scoring for these call categories — show the executive summary instead.
    addSectionHeading("Executive Summary");
    addFeedbackCard("Call Summary", executiveSummary || "Not available.");
  } else {
    addSectionHeading("Scores");
    const scoresStartPage = doc.internal.getCurrentPageInfo().pageNumber;
    addOverallScoreBanner();
    sections.forEach(addSectionTable);

    // Keep "Feedback" off the scoring page(s) so it reads as its own section.
    // Only force a fresh page when the scoring content actually fit on one —
    // if it already spilled onto a later page, let Feedback continue there
    // rather than leaving a blank page behind it.
    if (doc.internal.getCurrentPageInfo().pageNumber === scoresStartPage) {
      doc.addPage();
      y = MARGIN;
    }
  }

  addSectionHeading("Feedback");
  addFeedbackCard("What Went Well", whatWentWell);
  addFeedbackCard("What Can Improve", whatCanImprove);
  addFeedbackCard("General Comments", generalComments);

  /* ── Footer on every page ── */
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, pageHeight - 10, pageWidth - MARGIN, pageHeight - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("Magic Health — Call Review", MARGIN, pageHeight - 5);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 5, {
      align: "right",
    });
  }

  return doc;
};

export const downloadCallReviewPDF = async (data) => {
  const doc = await buildCallReviewPDF(data);
  const safeId = (data.magicHealthId || data.callId || "review")
    .toString()
    .replace(/[^a-z0-9-_]/gi, "_");
  doc.save(`Call_Review_Feedback_${safeId}.pdf`);
  return doc;
};

export default buildCallReviewPDF;
